import React, { useMemo } from 'react'
import { Button, Icon } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import prepareFilterParams from 'utils/prepareFilterParams'
import queryString from 'query-string'
import { useTranslation } from 'react-i18next'
import downloadFile from 'utils/downloadFile'
import { notificationShow } from 'modules/Notifications/actions'

const XlsDownloader = ({ noLabel = false, size = 'm', resource = '' }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const {
    sorting,
    currentResource,
    filters,
    campaignsSelection,
    adGroupsSelection,
    bannersSelection,
    currentItems,
  } = useSelector(
    (state) => ({
      sorting: state['services/campaigns-editor']?.[resource]?.sorting,
      currentResource: state['services/campaigns-editor'].currentResource,
      filters: state['services/campaigns-editor'].filters,
      campaignsSelection: state['services/campaigns-editor'].campaigns.selection.ids,
      adGroupsSelection: state['services/campaigns-editor']['ad-groups'].selection.ids,
      bannersSelection: state['services/campaigns-editor'].banners.selection.ids,
      currentItems: state['services/campaigns-editor']?.[resource].items || [],
    }),
    shallowEqual,
  )

  const campaignsSelectionIds = useMemo(() => Object.keys(campaignsSelection), [campaignsSelection])
  const adGroupsSelectionIds = useMemo(() => Object.keys(adGroupsSelection), [adGroupsSelection])
  const bannersSelectionIds = useMemo(() => Object.keys(bannersSelection), [bannersSelection])

  const sortingParams = useMemo(() => {
    const { id, desc } = sorting?.[0] ?? {}

    return id
      ? {
          sort: id,
          direction: desc ? 'desc' : 'asc',
        }
      : {}
  }, [sorting])

  const selectionParams = useMemo(() => {
    const params = {}

    switch (currentResource) {
      case 'campaigns':
        params.external_ids = campaignsSelectionIds
        break
      case 'ad-groups':
        if (adGroupsSelectionIds.length === 0) {
          params.campaign_external_ids = campaignsSelectionIds
        }
        params.external_ids = adGroupsSelectionIds
        break
      case 'banners':
        if (bannersSelectionIds.length === 0) {
          params.campaign_external_ids = campaignsSelectionIds
          params.ad_group_external_ids = adGroupsSelectionIds
        }
        params.external_ids = bannersSelectionIds
        break
    }

    return params
  }, [campaignsSelectionIds, adGroupsSelectionIds, currentResource])

  const fileUrl = useMemo(() => {
    const search = queryString.stringify(
      prepareFilterParams({
        ...filters,
        ...selectionParams,
        ...sortingParams,
        offset: 0,
        limit: 10000,
      }),
      {
        arrayFormat: 'bracket',
      },
    )
    return `/api/app/v1/vk-ads/${currentResource}/file?${search}`
  }, [filters, currentResource, selectionParams, sortingParams])

  return (
    <Button
      disabled={currentItems.length === 0}
      noLabel={noLabel}
      type={'button'}
      startIcon={<Icon name={'file-xls'} />}
      variant={'secondary'}
      link
      grey
      size={size}
      component={'a'}
      onClick={() =>
        currentItems.length === 0
          ? false
          : downloadFile(fileUrl, 'getuniq-vk-ads-campaigns', 'xls').catch(
              (error) =>
                dispatch(
                  notificationShow({
                    type: 'warning',
                    message: error,
                    delay: 20000,
                  }),
                ),
            )
      }
      target={'_blank'}
    >
      {t('campaigns_editor::download_button.label')}
    </Button>
  )
}

XlsDownloader.propTypes = {
  noLabel: PropTypes.bool,
  size: PropTypes.oneOf(['s', 'm', 'l']),
  sortingParams: PropTypes.object,
  resource: PropTypes.string,
}

export default XlsDownloader
